import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #selectionGeneration = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#selectionGeneration += 1;
    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const generation = ++this.#selectionGeneration;
    const { signal } = this.#controller;
    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html || signal.aborted || generation !== this.#selectionGeneration || !this.isConnected) return;

        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // DOMParser returns an inert document: decode() needs a fully active ownerDocument.
        // Import only this gallery so other ProductSelectEvent listeners keep their HTML intact.
        const preparedGallery = document.importNode(newMediaGallery, true);
        await this.#prepareSelectedImage(preparedGallery, signal);
        if (signal.aborted || generation !== this.#selectionGeneration || !this.isConnected) return;

        this.replaceWith(preparedGallery);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || signal.aborted || !this.isConnected) return;
        this.dispatchEvent(
          new ErrorEvent('error', {
            bubbles: true,
            error,
            message: error instanceof Error ? error.message : String(error),
          })
        );
      });
  };

  /**
   * Gives the selected preview at most 1500 ms to decode before swapping galleries.
   * @param {Element} gallery
   * @param {AbortSignal} signal
   */
  async #prepareSelectedImage(gallery, signal) {
    const image = gallery.querySelector(
      'slideshow-component slideshow-slide:not([hidden]) .product-media__image, .media-gallery__grid .product-media__image'
    );
    if (!(image instanceof HTMLImageElement)) return;

    image.loading = 'eager';
    await new Promise((resolve) => {
      /** @type {number | undefined} */
      let timeout;
      const finish = () => {
        window.clearTimeout(timeout);
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        signal.removeEventListener('abort', finish);
        resolve(undefined);
      };

      timeout = window.setTimeout(finish, 1500);
      signal.addEventListener('abort', finish, { once: true });
      if (signal.aborted) {
        finish();
      } else if (typeof image.decode === 'function') {
        try {
          image.decode().then(finish, finish);
        } catch {
          finish();
        }
      } else if (image.complete) {
        finish();
      } else {
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
      }
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
